import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ChevronDown, ChevronRight, User, Calendar,
  CheckCircle, XCircle, Clock, Slash
} from 'lucide-react';
import api from '../services/api';

const CancellationRequestsList = ({ permitId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: cancellationData, isLoading } = useQuery(
    ['cancellationRequests', permitId],
    async () => {
      const response = await api.get(`/cancellation-requests/permit/${permitId}/all`);
      return response.data;
    },
    {
      enabled: isExpanded
    }
  );

  const statusConfig = {
    'Pending_EAC_Review': {
      label: 'Pending EAC Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_SDO_Review': {
      label: 'Pending SDO Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_ADC_Review': {
      label: 'Pending ADC Review',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Pending_DC_Approval': {
      label: 'Pending DC Approval',
      color: 'bg-blue-100 text-blue-800',
      icon: Clock
    },
    'Approved': {
      label: 'Approved (Cancelled)',
      color: 'bg-green-100 text-green-800',
      icon: CheckCircle
    },
    'Rejected': {
      label: 'Rejected',
      color: 'bg-red-100 text-red-800',
      icon: XCircle
    },
    'Cancelled': {
      label: 'Withdrawn',
      color: 'bg-gray-100 text-gray-800',
      icon: XCircle
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const cancellationRequests = cancellationData?.cancellationRequests || [];

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Accordion Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
          <Slash className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Cancellation Request History
          </h3>
        </div>
        {!isExpanded && cancellationRequests.length > 0 && (
          <span className="text-sm text-gray-500">
            {cancellationRequests.length} request{cancellationRequests.length !== 1 ? 's' : ''}
          </span>
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="border-t">
          {isLoading ? (
            <div className="px-6 py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : cancellationRequests.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <Slash className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No cancellation requests found for this permit</p>
            </div>
          ) : (
            <div className="divide-y">
              {cancellationRequests.map((request) => {
                const statusInfo = statusConfig[request.status] || {
                  label: request.status,
                  color: 'bg-gray-100 text-gray-800',
                  icon: Clock
                };
                const StatusIcon = statusInfo.icon;

                return (
                  <div key={request.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Initiator & Date */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900">
                            {request.initiator?.fullName || 'Unknown'}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({request.initiatorRole?.toUpperCase()})
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-600">
                            Created: {formatDate(request.createdAt || request.created_at)}
                          </span>
                        </div>
                        {request.reason && (
                          <div className="text-sm text-gray-500 italic line-clamp-2">
                            "{request.reason}"
                          </div>
                        )}
                      </div>

                      {/* Right: Status & Decision */}
                      <div className="flex flex-col items-end gap-2">
                        {/* Status Badge */}
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                          <StatusIcon className="h-4 w-4 mr-1.5" />
                          {statusInfo.label}
                        </span>

                        {/* Final Decision Details */}
                        {request.status === 'Approved' && request.approver && (
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Approved by</div>
                            <div className="text-sm font-medium text-green-700">
                              {request.approver.fullName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(request.approvedAt || request.approved_at)}
                            </div>
                          </div>
                        )}

                        {request.status === 'Rejected' && request.rejector && (
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Rejected by</div>
                            <div className="text-sm font-medium text-red-700">
                              {request.rejector.fullName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(request.rejectedAt || request.rejected_at)}
                            </div>
                            {request.rejectionReason && (
                              <div className="text-xs text-gray-600 italic mt-1 max-w-xs">
                                "{request.rejectionReason}"
                              </div>
                            )}
                          </div>
                        )}

                        {/* View Link */}
                        <Link
                          to={`/cancellation-requests/${request.id}`}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CancellationRequestsList;
